import { GetServerSidePropsContext } from 'next';
import { Document } from '@contentful/rich-text-types';
import FundHero from '../../../../common-components/component-library/heroes/FundHero';
import { getApproaches, getInitiatives, getPillars } from '../../../../utils/api/content';
import { getImpacters } from '../../../../utils/api/impacter';
import { extractPlainTextFromRichText } from '../../../../utils/helpers/extractPlainTextToRichText';
import { getEntries } from '../../../../utils/api/contentful';
import {
  TypeCategoryPageSkeleton,
  TypeImpacterSkeleton,
  TypeInitiativeSkeleton,
  TypeMicrocopySetSkeleton,
  TypeProfile,
  TypePillarSkeleton,
} from '../../../../../@types/generated';
import { UrlError } from '../../../../utils/error';
import { getLocations } from '../../../../utils/api/location';
import { getLocationStrings } from '../../../../utils/helpers/locations';
import HighlightedProfilesLayout from '../../../../common-components/component-library/layout/highlightedProfilesLayout/HighlightedProfilesLayout';
import PillarAndApproachesLayout from '../../../../common-components/component-library/layout/PillarAndApproachesLayout';
import groupBy from 'lodash/groupBy';
import MapMetricsLayout from '../../../../common-components/component-library/layout/MapMetricsLayout';
import { getPostsCDN } from '../../../../utils/api/posts/posts';
import { POST_STATUS } from '../../../../utils/api/posts/types';
import ContentUpdateCarouselLayout from '../../../../common-components/component-library/content-updates/ContentUpdateCarouselLayout';
import ProjectListingWithPreview from '../../../../common-components/component-library/layout/ProjectList/ProjectListingWithPreview';
import { Asset } from 'contentful';
import RichText from '../../../../common-components/contentful-elements/RichText/RichText';
import { formatMicrocopy } from '../../../../utils/contentfulMicrocopy';
import { PILLAR_COLORS } from '../../../../utils/constants/constants';
import withAppProps, { WithPageAppProps } from '../../../../utils/withAppProps';
import { uniq } from 'lodash';
import { hasValue } from '../../../../utils/typeCheck';
import isCDRCategoryId from '../../../../utils/categories';

export type ServerSideProps = {
  props: {
    aboutTitle: string;
    pillarTitle: string;
    mapTitle: string;
    impacterTitle: string;
    updateTitle: string;
    projectListingTitle: string;
    projectListingCTATitle: string;
    projectListingCTADescription: string;
    projectListingCTAButtonLabel: string;
    fundSlug: string;
    title: string;
    heroImageUrl?: string;
    heroParagraph?: string;
    initiativeLogoUrl?: string;
    headline?: string;
    description?: string;
    initiativeManager?: {
      name?: string;
      title?: string;
      description?: Document;
      imageUrl?: string;
    };
    motivation?: Document;
    pillar?: {
      name: string;
      description?: string;
    };
    approaches: {
      name: string;
      description?: string;
    }[];
    mapItems: {
      label: string;
      locationCodes: string[];
    }[];
    impacters: {
      name?: string;
      title?: string | null;
      description?: string;
      imageUrl?: string;
    }[];
    posts: {
      id: string;
      publishedAt: string;
      caption: string | null;
      location?: string;
      imageSrc?: string;
      imageAlt: string;
      iconSrc?: string;
      iconAlt: string;
    }[];
    otherInitiatives: {
      id: string;
      pillarName?: string;
      pillarColor?: string;
      image?: Asset<'WITHOUT_UNRESOLVABLE_LINKS'>;
      iconUrl?: string;
      name: string;
      location?: string;
      description?: string;
      slug?: string;
    }[];
  };
} & WithPageAppProps;

export type InitiativePageProps = ServerSideProps['props'];

function InitiativePage({
  aboutTitle,
  pillarTitle,
  mapTitle,
  impacterTitle,
  updateTitle,
  projectListingTitle,
  projectListingCTATitle,
  projectListingCTADescription,
  projectListingCTAButtonLabel,
  fundSlug,
  title,
  heroImageUrl,
  heroParagraph,
  initiativeLogoUrl,
  headline,
  description,
  initiativeManager,
  motivation,
  pillar,
  approaches,
  mapItems,
  impacters,
  posts,
  otherInitiatives,
}: InitiativePageProps) {
  return (
    <>
      <FundHero
        title={title}
        imageUrl={heroImageUrl}
        imageOverlayIconUrl={initiativeLogoUrl}
        paragraph={heroParagraph}
        secondaryButtonLabel="Back to fund"
        secondaryButtonHref={`/impact-funds/${fundSlug}`}
      />
      <HighlightedProfilesLayout
        sectionTitle={aboutTitle}
        title={headline}
        paragraph={description}
        primaryProfilesHeadline="motivation"
        primaryProfiles={initiativeManager ? [initiativeManager] : undefined}
        profileDescriptionOverride={<RichText document={motivation} />}
      />
      {(pillar || approaches.length > 0) && (
        <PillarAndApproachesLayout
          sectionTitle={pillarTitle}
          pillar={pillar}
          approaches={approaches}
        />
      )}
      {mapItems.length > 1 && <MapMetricsLayout sectionTitle={mapTitle} mapItems={mapItems} />}
      {impacters && impacters.length > 0 && (
        <HighlightedProfilesLayout
          sectionTitle={impacterTitle}
          primaryProfiles={impacters.length < 3 ? impacters : undefined}
          secondaryProfiles={impacters.length >= 3 ? impacters : undefined}
        />
      )}
      {posts.length > 0 && <ContentUpdateCarouselLayout sectionTitle={updateTitle} posts={posts} />}
      <ProjectListingWithPreview
        sectionTitle={projectListingTitle}
        ctaTitle={projectListingCTATitle}
        ctaDescription={projectListingCTADescription}
        ctaButtonLabel={projectListingCTAButtonLabel}
        initiatives={otherInitiatives}
        fundSlug={fundSlug}
      />
    </>
  );
}

export async function getInitiativePageProps({
  impactFundSlug,
  initiativeSlug,
  HOST_URL,
}: {
  impactFundSlug: string;
  initiativeSlug: string;
  HOST_URL: string;
}) {
  if (!initiativeSlug || Array.isArray(initiativeSlug)) {
    throw new Error('No initiative slug provided');
  }

  const [
    {
      items: [contentfulInitiative],
    },
    {
      items: [contentfulCategory],
    },
  ] = await Promise.all([
    getEntries<TypeInitiativeSkeleton>({
      content_type: 'initiative',
      'fields.slug': `${initiativeSlug}`,
      include: 10,
    }),
    getEntries<TypeCategoryPageSkeleton>({
      content_type: 'categoryPage',
      'fields.slug': impactFundSlug,
    }),
  ]);

  if (!contentfulInitiative) {
    throw new UrlError(`No contentful initiative found for slug: ${initiativeSlug}`, {
      status: 404,
    });
  }
  const initiativeId = contentfulInitiative.sys.id;

  if (!contentfulCategory) {
    throw new UrlError(`No category found for slug ${impactFundSlug}`, { status: 404 });
  }
  const categoryId = contentfulCategory.sys.id;
  const isCDR = await isCDRCategoryId(categoryId);
  if (isCDR) {
    throw new UrlError(`Project does not exist for ${impactFundSlug}`, { status: 404 });
  }

  const {
    result: [initiative],
  } = await getInitiatives({
    initiativeId: [initiativeId],
    categoryId: [categoryId],
    skipContentfulData: true,
  });

  if (!initiative) {
    throw new UrlError(`Initiative ${initiativeId} does not belong to impact fund ${categoryId}`, {
      status: 404,
    });
  }

  const [initiativeLocations, pillars, impacters, posts] = await Promise.all([
    getLocations({ query: { code: initiative.locationCodes } }),
    getPillars({
      categoryId: [categoryId],
      skipContentfulData: true,
    }),
    getImpacters({ initiativeIds: [initiativeId], skipContentfulData: true }),
    getPostsCDN({
      initiativeId: [initiativeId],
      statuses: [POST_STATUS.PUBLISHED],
      tag: null,
      limit: 3,
    }),
  ]);

  const allCountries = getLocationStrings(initiativeLocations.result);

  const initiativePillars = pillars.result.filter((pillar) =>
    pillar.initiativeIds.includes(initiativeId),
  );
  const pillar = initiativePillars[0];

  const [
    approaches,
    {
      items: [contentfulPillar],
    },
    contentfulImpacters,
    microConfigSet,
    ...initiatives
  ] = await Promise.all([
    getApproaches({
      pillarId: [pillar?.id],
      initiativeId: [initiativeId],
    }),
    getEntries<TypePillarSkeleton>({
      content_type: 'pillar',
      'sys.id[in]': initiativePillars.map((pillar) => pillar.id),
    }),
    getEntries<TypeImpacterSkeleton>({
      content_type: 'impacter',
      'sys.id[in]': impacters.result.map((impacter) => impacter.externalId),
    }),
    getEntries<TypeMicrocopySetSkeleton>({
      content_type: 'microcopySet',
      'fields.name': 'projectPage',
    }),
    ...pillars.result.map(async (pillar) => {
      const fetchedInitiatives = await getInitiatives({
        pillarId: [pillar.id],
        limit: 2,
        status: ['ACTIVE'],
        skipContentfulData: true,
      });

      const otherInitiatives = fetchedInitiatives.result.filter(
        (initiative) => initiative.id !== initiativeId,
      );

      return otherInitiatives[0];
    }),
  ]);

  const otherInitiatives = initiatives.filter((initiative) => initiative !== undefined);

  const [otherContentfulInitiatives, otherInitiativeLocations] = await Promise.all([
    getEntries<TypeInitiativeSkeleton>({
      content_type: 'initiative',
      'sys.id[in]': otherInitiatives.map((initiative) => initiative.id),
    }),
    getLocations({
      query: { code: uniq(otherInitiatives.flatMap((initiative) => initiative.locationCodes)) },
    }),
  ]);

  const initiativeManager = contentfulInitiative.fields.motivation?.content.find(
    (node) =>
      node.nodeType === 'embedded-entry-block' &&
      node.data.target.sys.contentType.sys.id === 'profile',
  )?.data?.target as TypeProfile;

  const copy = formatMicrocopy(microConfigSet)['projectPage'];
  const locationsByCountry = Object.entries(groupBy(initiativeLocations.result, 'country.name'));

  const firstFourMapItems = locationsByCountry.slice(0, 4).map(([country, locations]) => ({
    label: country,
    locationCodes: locations.map((location) => location.code),
  }));

  const fifthMapItem = locationsByCountry
    .slice(4)
    .reduce<{ label: string; locationCodes: string[] }>(
      (acc, [country, locations], index, array) => {
        if (array.length === 1) {
          return {
            label: country,
            locationCodes: locations.map((location) => location.code),
          };
        }

        const locationCodes = locations.map((location) => location.code);

        return {
          label: 'Other',
          locationCodes: [...acc.locationCodes, ...locationCodes],
        };
      },
      { label: '', locationCodes: [] },
    );

  const mapItems =
    locationsByCountry.length > 4 ? [...firstFourMapItems, fifthMapItem] : firstFourMapItems;

  return {
    props: {
      aboutTitle: copy.aboutTitle,
      pillarTitle: copy.pillarTitle,
      mapTitle: copy.mapTitle,
      impacterTitle: copy.impacterTitle,
      updateTitle: copy.updateTitle,
      projectListingTitle: copy.projectListingTitle,
      projectListingCTATitle: copy.projectListingCTATitle,
      projectListingCTADescription: copy.projectListingCTADescription,
      projectListingCTAButtonLabel: copy.projectListingCTAButtonLabel,
      fundSlug: impactFundSlug,
      title: contentfulInitiative.fields.title ?? initiative.name,
      heroImageUrl: contentfulInitiative.fields.image?.fields.file?.url,
      heroParagraph: `${contentfulCategory.fields.title} • ${
        allCountries.countriesTruncated.length
          ? allCountries.countriesTruncated
          : allCountries.truncated
      }`,
      initiativeLogoUrl: contentfulInitiative.fields.logo?.fields.file?.url,
      headline: contentfulInitiative.fields.tagline,
      description: contentfulInitiative.fields.description,
      initiativeManager: initiativeManager
        ? {
            name: initiativeManager?.fields.name,
            title: initiativeManager?.fields.title,
            description: initiativeManager?.fields.profileDescription,
            imageUrl:
              initiativeManager?.fields.cutoutImage?.fields.file?.url ??
              initiativeManager?.fields.image?.fields.file?.url,
          }
        : undefined,
      motivation: contentfulInitiative.fields.motivation,
      pillar: contentfulPillar
        ? {
            name: contentfulPillar.fields.title ?? pillar?.name,
            description: contentfulPillar.fields.description,
          }
        : undefined,
      approaches: approaches.result.map((approach) => ({
        name: extractPlainTextFromRichText(approach.title) ?? approach.name,
        description: extractPlainTextFromRichText(approach.description),
      })),
      mapItems,
      impacters: impacters.result.map((impacter) => {
        const contentfulImpacter = contentfulImpacters.items.find(
          (imp) => imp.sys.id === impacter.externalId,
        );
        return {
          name: contentfulImpacter?.fields.name ?? impacter.name,
          title: contentfulImpacter?.fields.organisation ?? impacter.organisationName,
          description: contentfulImpacter?.fields.shortIntroduction,
          imageUrl:
            contentfulImpacter?.fields.cutoutImage?.fields.file?.url ??
            contentfulImpacter?.fields.image?.fields.file?.url,
        };
      }),
      posts: posts.posts
        .filter((post) => post.assets?.length && post.assets?.some((elem) => elem.type !== 'TEXT'))
        .map((post) => ({
          id: post.id,
          publishedAt: post.publishedAt,
          caption: post.caption,
          location: contentfulInitiative.fields.location,
          imageSrc: post.assets?.[0].image?.src || post.assets?.[0].video?.posters?.main,
          imageAlt: post.caption ?? '',
          iconSrc: contentfulInitiative.fields.logo?.fields.file?.url,
          iconAlt:
            contentfulInitiative.fields.logo?.fields.description ??
            contentfulInitiative.fields.logo?.fields.title ??
            '',
        })),
      otherInitiatives: otherInitiatives.map((initiative, index) => {
        const contentfulInitiative = otherContentfulInitiatives.items.find(
          (contentfulIni) => contentfulIni.sys.id === initiative.id,
        );
        const pillar = pillars.result.find((pillar) =>
          pillar.initiativeIds.includes(initiative.id),
        );
        const locations = initiative.locationCodes
          .map((code) => otherInitiativeLocations.result.find((location) => location.code === code))
          .filter(hasValue);
        const { truncated, countriesTruncated } = getLocationStrings(locations);
        return {
          id: initiative.id,
          pillarName: contentfulPillar?.fields.title ?? pillar?.name,
          pillarColor: PILLAR_COLORS[index % PILLAR_COLORS.length],
          image: contentfulInitiative?.fields.image,
          iconUrl: contentfulInitiative?.fields.logo?.fields.file?.url,
          name: contentfulInitiative?.fields.title ?? initiative.name,
          location: countriesTruncated.length ? countriesTruncated : truncated,
          description: contentfulInitiative?.fields.description,
          slug: contentfulInitiative?.fields.slug,
        };
      }),
    },
    pageAppProps: {
      metaHeader: {
        seoTitle: contentfulInitiative.fields.title
          ? `${contentfulInitiative.fields.title} | ${contentfulCategory.fields.title}`
          : '',
        seoDescription: contentfulInitiative.fields.description
          ? `${contentfulInitiative.fields.description} | ${contentfulCategory.fields.title}`
          : '',
        socialTitle: contentfulInitiative.fields.title
          ? `${contentfulInitiative.fields.title} | ${contentfulCategory.fields.title}`
          : '',
        socialDescription: contentfulInitiative.fields.description
          ? `${contentfulInitiative.fields.description} | ${contentfulCategory.fields.title}`
          : '',
        socialImageUrl: contentfulInitiative.fields.image?.fields.file?.url ?? '',
        socialImageHeight: 0,
        socialImageWidth: 0,
        canonicalLink: `${HOST_URL}/impact-funds/${impactFundSlug}/projects/${initiativeSlug}`,
      },
    },
  };
}

export const getServerSideProps = withAppProps(
  async (ctx: GetServerSidePropsContext, config): Promise<ServerSideProps> => {
    const name = ctx?.params?.name as string;
    const initiativeSlug = ctx?.params?.slug as string;

    return await getInitiativePageProps({
      impactFundSlug: name,
      initiativeSlug,
      HOST_URL: config.HOST_URL,
    });
  },
);

export default InitiativePage;
